import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Box,
    Typography,
} from '@mui/material';
import { IMGSEARCHV2 } from '../../imageSearchV2.classes';
import { SCUIImageThumbnailV2 } from '../SCUIImageThumbnail/SCUIImageThumbnailV2';

interface Product {
    id: number;
    img: string;
    barcode: string;
    specialCode: string;
    colorCode: string;
    seasonCode: string;
    productName: string;
    merchAgeGroup: string;
    size: string;
    line: string;
    currentStatus: string;
    productStatus: string;
    setSelectedItems: any[];
    imagePathIndexMetas: {
        uri?: string | null;
        path?: string | null;
        imageSrc?: string;
    };
}

interface Props {
    products: Product[];
    selectedItems: any[];
    setSelectedItems: React.Dispatch<React.SetStateAction<any[]>>;
    onPreviewImage: (uri: string) => void;
    disabled?: boolean;
}

const DATA_COLUMN_COUNT = 8;

export const SCUIProductListV2: React.FC<Props> = ({
    products,
    selectedItems,
    setSelectedItems,
    onPreviewImage,
    disabled = false,
}) => {
    const toggleSelection = (item: any) => {
        if (disabled) {
            return;
        }
        if (selectedItems.includes(item)) {
            setSelectedItems(selectedItems.filter((i) => i !== item));
        } else {
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleSelectClick = (event: React.MouseEvent, item: any) => {
        event.stopPropagation();
        toggleSelection(item);
    };

    return (
        <TableContainer className={IMGSEARCHV2.productListPaper} component={Paper}>
            <Table className={IMGSEARCHV2.productListTable}>
                <TableHead>
                    <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Barcode</TableCell>
                        <TableCell>Special Code</TableCell>
                        <TableCell>Color Code</TableCell>
                        <TableCell>Season Code</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>Merge Age Group</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Line</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((product) => {
                        const imageSrc =
                            product.imagePathIndexMetas.imageSrc ||
                            product.imagePathIndexMetas.uri ||
                            '';
                        const pathLabel = (product.imagePathIndexMetas?.path || '').trim();
                        const rowKey = `${product.id}-${product.barcode}-${pathLabel}`;

                        return (
                            <React.Fragment key={rowKey}>
                                <TableRow>
                                    <TableCell
                                        rowSpan={pathLabel ? 2 : 1}
                                        sx={{
                                            verticalAlign: 'top',
                                            ...(pathLabel ? { borderBottom: 'none' } : {}),
                                        }}
                                    >
                                        <SCUIImageThumbnailV2
                                            src={imageSrc}
                                            onPreview={onPreviewImage}
                                            onSelect={() => toggleSelection(product)}
                                            disabled={disabled}
                                            className={IMGSEARCHV2.productImageCell}
                                            containerStyle={{
                                                width: 100,
                                                height: 100,
                                                borderRadius: '6px',
                                            }}
                                        >
                                            <Checkbox
                                                className={IMGSEARCHV2.productRowCheckbox}
                                                checked={selectedItems.includes(product)}
                                                onClick={(event) => handleSelectClick(event, product)}
                                                disabled={disabled}
                                                style={{
                                                    position: 'absolute',
                                                    top: '0.25rem',
                                                    left: '0.25rem',
                                                    zIndex: 2,
                                                }}
                                            />
                                        </SCUIImageThumbnailV2>
                                    </TableCell>

                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.barcode}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.specialCode}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.colorCode}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.seasonCode}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.productName}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.merchAgeGroup}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.size}
                                    </TableCell>
                                    <TableCell sx={pathLabel ? { borderBottom: 'none' } : undefined}>
                                        {product.line}
                                    </TableCell>
                                </TableRow>
                                {pathLabel ? (
                                    <TableRow>
                                        <TableCell
                                            className={IMGSEARCHV2.productListPathCell}
                                            colSpan={DATA_COLUMN_COUNT}
                                            sx={{
                                                py: 0.75,
                                                pt: 0,
                                                borderTop: 'none',
                                            }}
                                        >
                                            <Box
                                                className={IMGSEARCHV2.productListPathRibbon}
                                                title={pathLabel}
                                                sx={{
                                                    width: '100%',
                                                    px: 1.25,
                                                    py: 0.65,
                                                    borderRadius: '6px',
                                                    bgcolor: 'rgba(15, 23, 42, 0.08)',
                                                    border: '1px solid #E2E8F0',
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        color: '#334155',
                                                        lineHeight: 1.4,
                                                        letterSpacing: '0.01em',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {pathLabel}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
